import {useCallback, useEffect, useState} from "react";
import {deleteBackup, searchBackups, uploadBackup} from "@/api/BackupApi.ts";
import {Backup} from "@/backup/Backup.ts";
import {notifyError} from "@/api/ErrorNotification.ts";

export type BackupMutation = "upload" | "delete";

export function useBackups() {
    const [backups, setBackups] = useState<Backup[]>([]);
    const [pendingMutation, setPendingMutation] = useState<BackupMutation | null>(null);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        try {
            const result = await searchBackups({page: 0, size: 50, sort: "createdAt,desc"});
            setBackups(result.content ?? []);
            setError(null);
            return true;
        } catch {
            setError("Die Backup-Liste konnte nicht geladen werden.");
            return false;
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        searchBackups({page: 0, size: 50, sort: "createdAt,desc"})
            .then(result => {
                if (!cancelled) setBackups(result.content ?? []);
            })
            .catch(() => {
                if (!cancelled) setError("Die Backup-Liste konnte nicht geladen werden.");
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const runMutation = useCallback(async (
        mutation: BackupMutation,
        operation: () => Promise<void>,
    ): Promise<boolean> => {
        if (pendingMutation) return false;
        setPendingMutation(mutation);
        setError(null);
        try {
            await operation();
            await refresh();
            return true;
        } catch (reason) {
            notifyError(reason, mutation === "upload"
                ? "Das Backup konnte nicht hochgeladen werden."
                : "Das Backup konnte nicht gelöscht werden.");
            return false;
        } finally {
            setPendingMutation(null);
        }
    }, [pendingMutation, refresh]);

    const upload = useCallback((file: File) => runMutation("upload", async () => {
        await uploadBackup(file);
    }), [runMutation]);
    const remove = useCallback((backupId: string) => runMutation("delete", async () => {
        await deleteBackup(backupId);
    }), [runMutation]);

    return {backups, pendingMutation, error, refresh, upload, remove};
}
